import { AlertCircle, Loader2, X } from 'lucide-react';
import React, { useState } from 'react';
import { confirmLoginCode, redirectToProvider, requestLoginCode } from './allauth';
import logo from '@/assets/logos/r41-primary.png';
import learn41Logo from '@/assets/tenant-logos/learn41.png';
import { isLearn41Domain } from './useTenant';
import { EllipsedEmail } from '@/common/components/EllipsedEmail';

interface LoginModalProps {
  description?: string;
  contextDescription?: string;
  jobTitle?: string;
  companyName?: string;
  hideCloseButton?: boolean;
  isInline?: boolean;
  isDashboardLogin?: boolean;
  isAssessment?: boolean;
  onClose?: () => void;
  isProtectedRoute?: boolean;
}

const LoginModal: React.FC<LoginModalProps> = ({
  jobTitle,
  companyName,
  hideCloseButton = false,
  isInline = false,
  isDashboardLogin = false,
  isAssessment = false,
  onClose,
  isProtectedRoute = true
}) => {
  const [email, setEmail] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | React.ReactNode>(null);
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const [isConfirmingCode, setIsConfirmingCode] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const currentUrl = window.location.href;

  const handleClose = () => {
    if (onClose) {
      // Use custom close handler if provided
      onClose();
    } else if (isProtectedRoute) {
      // Protected route context - navigate back (default behavior)
      window.history.back();
    }
    // If no onClose provided and not protected route, do nothing
    // (parent component should handle modal visibility)
  };

  const handleEmailRequest = async () => {
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setError(null);
    setIsRequestingCode(true);

    try {
      await requestLoginCode(email);
      setCodeSent(true);
      setSubmittedEmail(email);
    } catch (err: any) {
      const status = err?.response?.status || 500;

      console.error('[LoginModal.handleEmailRequest] Error requesting login code:', err);

      // Set specific error messages based on status code
      let errorMessage = 'Unable to send verification code. Please try again.';

      if (status >= 500) {
        errorMessage = 'Server error. Please try again in a moment.';
        setError(errorMessage);
      } else {
        // Handle client errors with specific messages
        switch (status) {
          case 400:
            errorMessage = 'Invalid email format. Please enter a valid email address.';
            setError(errorMessage);
            break;
          case 422:
            errorMessage = 'Email address is not accepted. Please use a different email.';
            setError(errorMessage);
            break;
          case 429:
            errorMessage = 'Too many requests. Please wait a moment before trying again.';
            setError(errorMessage);
            break;
          default:
            // For other client errors (including enumeration prevention), show code sent state
            setCodeSent(true);
            setSubmittedEmail(email);
        }
      }
    }

    setIsRequestingCode(false);
  };

  const handleConfirmCode = async () => {
    if (!code) {
      setError('Please enter the verification code.');
      return;
    }

    setError(null);
    setIsConfirmingCode(true);

    try {
      const response = await confirmLoginCode(code);

      // Check if the response indicates success
      if (response.status === 200 && response.meta?.is_authenticated) {
        // Check for ?next redirect (e.g., from /docs/ auth redirect)
        const nextUrl = new URLSearchParams(window.location.search).get('next');
        if (nextUrl && nextUrl.startsWith('/docs/')) {
          window.location.href = nextUrl;
        } else {
          window.location.reload();
        }
      } else {
        // Handle error response with specific messages based on status code
        console.error('[LoginModal.handleConfirmCode] Code confirmation failed:', response);

        // Set specific error messages based on status code
        let errorMessage = 'Invalid verification code. Please try again.';

        switch (response.status) {
          case 400:
            errorMessage = 'Invalid verification code format. Please enter a valid code.';
            break;
          case 401:
            errorMessage = 'Invalid verification code. Please try again.';
            break;
          case 404:
            errorMessage = 'Verification session expired. Please request a new code.';
            // Reset to email entry for expired sessions
            setCodeSent(false);
            setSubmittedEmail('');
            break;
          case 410:
            errorMessage = 'Verification session expired. Please request a new code.';
            // Reset to email entry for expired sessions
            setCodeSent(false);
            setSubmittedEmail('');
            break;
          case 429:
            errorMessage = 'Too many attempts. Please wait a moment before trying again.';
            break;
          case 422:
            errorMessage = 'Verification code has already been used. Please request a new one.';
            // Reset to email entry for used codes
            setCodeSent(false);
            setSubmittedEmail('');
            break;
          case 500:
          case 502:
          case 503:
            errorMessage = 'Server error. Please try again in a moment.';
            break;
          default:
            errorMessage = 'Invalid verification code. Please try again.';
        }

        setError(errorMessage);

        // Clear the code input for most errors (unless we're resetting to email entry)
        if (response.status !== 404 && response.status !== 410 && response.status !== 422) {
          setCode('');
        }
      }
    } catch (err) {
      // Handle actual network/parsing errors
      console.error('[LoginModal.handleConfirmCode] Network/parsing error:', err);
      setError('Something went wrong. Please try again.');
      setCode('');
    }

    setIsConfirmingCode(false);
  };

  const formContent = (
    <>
      {!hideCloseButton && !isInline && (
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Close"
        >
          <X className="h-6 w-6"/>
        </button>
      )}

      <div className="text-center mb-6">
        <p className="text-gray-500 text-sm mb-2 uppercase tracking-wide">
          WELCOME TO{!isDashboardLogin && !companyName && ' YOUR'}
        </p>
        {companyName && !isDashboardLogin ? (
          <h2 className="text-2xl font-bold text-primary-400 mb-1">
            {companyName}
          </h2>
        ) : isDashboardLogin ? (
          <>
            <div className="flex items-center justify-center mb-4">
              {isLearn41Domain() ? (
                <img
                  src={learn41Logo}
                  alt="Learn41 Logo"
                  className="h-16"
                />
              ) : (
                <>
                  <img
                    src={logo}
                    alt="Recruit41 Logo"
                    className="h-8 w-8"
                  />
                  <h2 className="text-2xl font-bold text-primary-400 -ml-1">
                    ecruit41
                  </h2>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            {(jobTitle || !isAssessment) && (
              <h2 className="text-2xl font-bold text-primary-400 mb-1">
                {jobTitle || 'Position'}
              </h2>
            )}
            <p className="text-2xl font-semibold text-gray-900 mb-1">
              {isLearn41Domain() ? 'Test' : isAssessment ? 'Assessment' : 'Interview'}
            </p>
          </>
        )}
        <div className="relative mb-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-200"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-white px-3 text-sm text-gray-600">
              Please login to continue
            </span>
          </div>
        </div>
      </div>

        {error && (
          <div className="mb-6 p-3 bg-red-50 border-l-4 border-red-500 rounded-md flex items-start">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5"/>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {codeSent && (
          <>
            <div className="space-y-2.5 mb-5">
              <label htmlFor="emailDisplay" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <div
                id="emailDisplay"
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-600 text-sm"
              >
                <EllipsedEmail email={submittedEmail} className="w-full" />
              </div>
            </div>
            <div className="mb-5">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="mb-6 text-sm text-gray-600 text-center">
              We've sent a 6-digit code to <br/>
              <strong className="block max-w-full text-gray-900">
                <EllipsedEmail email={submittedEmail} className="max-w-full" />
              </strong>
            </div>
          </>
        )}

        {!codeSent ? (
          <>
            <div className="space-y-2.5 mb-5">
              <button
                onClick={() => redirectToProvider('google', currentUrl)}
                className="w-full bg-white border border-gray-300 hover:border-primary-400 hover:bg-primary-50 text-gray-800 py-2.5 px-4 rounded-xl flex items-center justify-center gap-3 transition-all font-medium text-sm"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.7 7.31 9.13 5.38 12 5.38z"
                  />
                </svg>
                Continue with Google
              </button>

              <button
                onClick={() => redirectToProvider('microsoft', currentUrl)}
                className="w-full bg-white border border-gray-300 hover:border-primary-400 hover:bg-primary-50 text-gray-800 py-2.5 px-4 rounded-xl flex items-center justify-center gap-3 transition-all font-medium text-sm"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#f25022" d="M1 1h10v10H1z"/>
                  <path fill="#00a4ef" d="M13 1h10v10H13z"/>
                  <path fill="#7fba00" d="M1 13h10v10H1z"/>
                  <path fill="#ffb900" d="M13 13h10v10H13z"/>
                </svg>
                Continue with Microsoft
              </button>
            </div>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs font-medium text-gray-500">
                  OR
                </span>
              </div>
            </div>

            <div className="space-y-2.5">
              <input
                id="email"
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleEmailRequest();
                  }
                }}
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-sm text-gray-900 placeholder-gray-400"
              />

              <button
                onClick={handleEmailRequest}
                disabled={isRequestingCode}
                className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all font-medium text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isRequestingCode ? 'Sending...' : 'Send Verification Code'}
              </button>
            </div>
          </>
        ) : (
          <div className="space-y-4">
            <div>
              <label
                htmlFor="code"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Verification Code
              </label>
              <input
                id="code"
                type="text"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmCode();
                  }
                }}
                className="w-full border border-gray-300 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all text-gray-900 text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>

            <button
              onClick={handleConfirmCode}
              disabled={isConfirmingCode}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition-all font-medium text-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isConfirmingCode ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin"/>
                  Verifying...
                </>
              ) : (
                'Verify & Continue'
              )}
            </button>

            <button
              onClick={() => {
                setCodeSent(false);
                setCode('');
                setError(null);
                setSubmittedEmail('');
              }}
              className="w-full text-sm text-gray-600 hover:text-gray-900 transition-colors py-2 mt-2"
            >
              Use a different email
            </button>
          </div>
        )}

        <div className="text-xs text-center text-gray-500 mt-5 pt-5 border-t border-gray-100">
          By continuing, you agree to Recruit41's{' '}
          <a
            href="https://docs.google.com/document/d/e/2PACX-1vTC4hPHpMdmyhSaVEkcf7t5IALiOxzTDIyF_0oVxkFzuWTeZd_xalJX-4jjTrvje51grw5SmFyMGOkw/pub"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 underline"
          >
            Terms of Service
          </a>
          {' '}and{' '}
          <a
            href="https://docs.google.com/document/d/e/2PACX-1vTC4hPHpMdmyhSaVEkcf7t5IALiOxzTDIyF_0oVxkFzuWTeZd_xalJX-4jjTrvje51grw5SmFyMGOkw/pub"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-700 underline"
          >
            Privacy Policy
          </a>
          .
        </div>
    </>
  );

  // If inline mode, just return the form content without modal wrapper
  if (isInline) {
    return formContent;
  }

  // Default modal with full-screen backdrop
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm z-50">
      <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full relative animate-fadeIn">
        {formContent}
      </div>
    </div>
  );
};

export default LoginModal;
